/**
 * ⌘K — die Befehlssuche (Redesign 03.09.2026, Richtung „Kompakte Konsole").
 *
 * Für alle, die das System kennen, ersetzt sie das Menü: Kunde tippen, Ziel
 * tippen, Enter. Zwei Tastendrücke zu jeder Seite und zu jedem Kunden-Tab.
 * Die Sidebar bleibt als Rückversicherung für Neue.
 *
 * Bewusst ohne Fremdbibliothek: eine Liste, ein Filter, drei Tasten.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Building2, Compass, CornerDownLeft } from 'lucide-react';
import { useAuthStore } from '@/hooks/useAuth';
import { getCustomers } from '@/lib/api';

interface Ctx { open: () => void }
const PaletteContext = createContext<Ctx>({ open: () => {} });
export const useCommandPalette = () => useContext(PaletteContext);

type Treffer = { id: string; gruppe: 'Kunden' | 'Seiten'; titel: string; hinweis?: string; ziel: string };

/** Alle Seiten des Systems — damit „Finanzen" oder „CRM" per Tastatur erreichbar bleibt. */
const SEITEN_ADMIN: Array<{ titel: string; ziel: string; hinweis?: string }> = [
  { titel: 'Leitwarte', ziel: '/leitwarte', hinweis: 'Master-Dashboard: Vorgänge, Kunden, Geld, Daten' },
  { titel: 'Heute (ausgeblendet)', ziel: '/heute', hinweis: 'alte Startseite, in der Leitwarte aufgegangen' },
  { titel: 'Kunden', ziel: '/kunden', hinweis: 'Liste mit Ampeln' },
  { titel: 'Alle Anfragen', ziel: '/leads' },
  { titel: 'CRM', ziel: '/crm', hinweis: 'Kaltakquise, Anfragen, Potenzial' },
  { titel: 'Betriebe', ziel: '/betriebe', hinweis: 'Master-CRM: Leadliste, Chancen, Pipeline' },
  { titel: 'Vertriebsanalyse (ausgeblendet)', ziel: '/vertrieb/analyse', hinweis: 'Wochenzahlen, in der Leitwarte aufgegangen' },
  { titel: 'Vertriebsablauf', ziel: '/todos', hinweis: 'Was heute dran ist, der Ablauf und die Regeln' },
  { titel: 'Aufgaben und Projekte', ziel: '/todos/aufgaben' },
  { titel: 'Potenzialanalyse', ziel: '/potenzial' },
  { titel: 'Live', ziel: '/analytics/live', hinweis: 'Wer gerade auf den Websites ist und woher er kommt' },
  { titel: 'Finanzplan', ziel: '/finanzen', hinweis: 'Posten bearbeiten, Kontostand pflegen' },
  { titel: 'Abrechnung', ziel: '/abrechnung' },
  { titel: 'Statistiken (ausgeblendet)', ziel: '/statistiken', hinweis: 'in der Leitwarte aufgegangen' },
  { titel: 'Analytics gesamt (ausgeblendet)', ziel: '/analytics', hinweis: 'alle Kunden im Vergleich, siehe Leitwarte › Daten' },
  { titel: 'Projekte', ziel: '/projects' },
  { titel: 'Einrichtung', ziel: '/einrichtung', hinweis: 'Tracking, Search Console, Ads' },
  { titel: 'Formulare', ziel: '/submissions', hinweis: 'unzugeordnete Eingänge' },
  { titel: 'Monatsberichte', ziel: '/monatsbericht' },
  { titel: 'Kunden verwalten', ziel: '/kunden/verwalten', hinweis: 'anlegen, einladen, bearbeiten' },
  { titel: 'Dokumentation', ziel: '/dokumentation' },
];
/** Rolle „vertrieb" (06.09.2026): nur der Vertriebs-Ausschnitt — wie in der Sidebar. */
const SEITEN_VERTRIEB: Array<{ titel: string; ziel: string; hinweis?: string }> = [
  { titel: 'Betriebe', ziel: '/betriebe', hinweis: 'Leadliste, Chancen, Pipeline' },
  { titel: 'Vertriebsanalyse (ausgeblendet)', ziel: '/vertrieb/analyse', hinweis: 'Wochenzahlen, in der Leitwarte aufgegangen' },
  { titel: 'CRM', ziel: '/crm', hinweis: 'Anfragen, Potenzial' },
  { titel: 'Vertriebsablauf', ziel: '/todos', hinweis: 'Was heute dran ist, der Ablauf und die Regeln' },
  { titel: 'Aufgaben und Projekte', ziel: '/todos/aufgaben' },
];
const SEITEN_KUNDE: Array<{ titel: string; ziel: string; hinweis?: string }> = [
  { titel: 'Anfragen', ziel: '/leads' },
  { titel: 'Analytics', ziel: '/analytics' },
  { titel: 'Monatsbericht', ziel: '/monatsbericht' },
  { titel: 'Formulare', ziel: '/submissions' },
  { titel: 'Abrechnung', ziel: '/abrechnung' },
];
/** Die Tabs des Kunden-Arbeitsbereichs — „thomas ads" springt direkt hinein. */
const KUNDEN_TABS: Array<{ key: string; titel: string; suchwoerter: string }> = [
  { key: 'uebersicht', titel: 'Übersicht', suchwoerter: 'übersicht uebersicht' },
  { key: 'anfragen', titel: 'Anfragen', suchwoerter: 'anfragen leads' },
  { key: 'ads', titel: 'Google Ads', suchwoerter: 'ads google kampagne kampagnen werbung' },
  { key: 'seo', titel: 'SEO & Blog', suchwoerter: 'seo blog google suche publivo strategie' },
  { key: 'bericht', titel: 'Monatsbericht', suchwoerter: 'bericht monatsbericht report' },
  { key: 'empfehlungen', titel: 'Empfehlungen', suchwoerter: 'empfehlungen ki optimierung' },
  { key: 'abrechnung', titel: 'Abrechnung', suchwoerter: 'abrechnung rechnung stripe' },
  { key: 'einstellungen', titel: 'Einstellungen', suchwoerter: 'einstellungen projekte einrichtung' },
];

const norm = (s: string) => s.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss');

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [offen, setOffen] = useState(false);
  const [eingabe, setEingabe] = useState('');
  const [aktiv, setAktiv] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const isVertrieb = user?.role === 'vertrieb';
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: kunden } = useQuery({ queryKey: ['customers'], queryFn: getCustomers, enabled: isAdmin && offen, staleTime: 300_000 });

  const open = useCallback(() => { setEingabe(''); setAktiv(0); setOffen(true); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); offen ? setOffen(false) : open(); }
      else if (e.key === 'Escape' && offen) setOffen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [offen, open]);

  useEffect(() => { if (offen) setTimeout(() => inputRef.current?.focus(), 10); }, [offen]);

  const treffer = useMemo<Treffer[]>(() => {
    const q = norm(eingabe.trim());
    const woerter = q.split(/\s+/).filter(Boolean);
    const seiten = isAdmin ? SEITEN_ADMIN : isVertrieb ? SEITEN_VERTRIEB : SEITEN_KUNDE;
    const aus: Treffer[] = [];

    if (isAdmin && kunden) {
      for (const k of kunden) {
        const name = norm(k.name);
        // Erstes Wort muss den Kunden treffen, die übrigen dürfen einen Tab meinen.
        const [erstes, ...rest] = woerter;
        const kundeTrifft = !erstes || name.includes(erstes) || norm(k.slug || '').includes(erstes);
        if (!kundeTrifft) continue;
        const tabWort = rest.join(' ');
        // Wortanfang statt Teilstring: „ads" darf nicht über „le-ads" die Anfragen treffen.
        const trifftTab = (t: typeof KUNDEN_TABS[number]) => norm(`${t.titel} ${t.suchwoerter}`).split(/\s+/).some((w) => w.startsWith(tabWort));
        const tabs = tabWort ? KUNDEN_TABS.filter(trifftTab) : [];
        if (tabs.length) {
          for (const t of tabs) aus.push({ id: `${k.id}-${t.key}`, gruppe: 'Kunden', titel: `${k.name} › ${t.titel}`, ziel: `/kunden/${k.id}?tab=${t.key}` });
        } else if (!tabWort) {
          aus.push({ id: k.id, gruppe: 'Kunden', titel: k.name, hinweis: k.active ? undefined : 'inaktiv', ziel: `/kunden/${k.id}` });
        }
      }
    }
    for (const s of seiten) {
      if (!q || woerter.every((w) => norm(s.titel).includes(w) || norm(s.hinweis || '').includes(w))) {
        aus.push({ id: s.ziel, gruppe: 'Seiten', titel: s.titel, hinweis: s.hinweis, ziel: s.ziel });
      }
    }
    return aus.slice(0, 12);
  }, [eingabe, kunden, isAdmin, isVertrieb]);

  useEffect(() => { setAktiv(0); }, [eingabe]);

  const waehle = (t: Treffer) => { setOffen(false); navigate(t.ziel); };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setAktiv((a) => Math.min(a + 1, treffer.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setAktiv((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && treffer[aktiv]) { e.preventDefault(); waehle(treffer[aktiv]); }
  };

  let letzteGruppe = '';
  return (
    <PaletteContext.Provider value={{ open }}>
      {children}
      {offen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center bg-gray-900/40 pt-[12vh] px-4" onMouseDown={() => setOffen(false)}>
          <div className="w-full max-w-xl rounded-xl bg-white shadow-2xl border border-gray-200 overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                ref={inputRef}
                value={eingabe}
                onChange={(e) => setEingabe(e.target.value)}
                onKeyDown={onInputKey}
                placeholder={isAdmin ? 'Kunde, Seite oder Aktion — z. B. „thomas ads"' : 'Seite suchen…'}
                className="flex-1 text-[15px] outline-none placeholder:text-gray-400"
              />
              <kbd className="text-[11px] text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">esc</kbd>
            </div>
            <div className="max-h-[50vh] overflow-y-auto py-1">
              {treffer.length === 0 && <div className="px-4 py-6 text-sm text-gray-400 text-center">Nichts gefunden.</div>}
              {treffer.map((t, i) => {
                const kopf = t.gruppe !== letzteGruppe; letzteGruppe = t.gruppe;
                return (
                  <div key={t.id}>
                    {kopf && <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t.gruppe}</div>}
                    <button
                      onMouseEnter={() => setAktiv(i)}
                      onClick={() => waehle(t)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm ${i === aktiv ? 'bg-gray-900 text-white' : 'text-gray-800 hover:bg-gray-50'}`}
                    >
                      {t.gruppe === 'Kunden' ? <Building2 className="w-4 h-4 flex-shrink-0 opacity-70" /> : <Compass className="w-4 h-4 flex-shrink-0 opacity-70" />}
                      <span className="flex-1 truncate font-medium">{t.titel}</span>
                      {t.hinweis && <span className={`text-xs ${i === aktiv ? 'text-gray-300' : 'text-gray-400'}`}>{t.hinweis}</span>}
                      {i === aktiv && <CornerDownLeft className="w-3.5 h-3.5 opacity-60" />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </PaletteContext.Provider>
  );
}
